"use client";

import React from "react";
import {
  Users,
  Shield,
  UserCheck,
  Shirt,
} from "lucide-react";

export default function PlayingXI({ data }) {

  const homeTeam = {
    name: data?.match_hometeam_name,
    logo: data?.team_home_badge,
    formation: data?.match_hometeam_system,
    players:
      data?.lineup?.home?.starting_lineups || [],
    coach: data?.lineup?.home?.coach || [],
  };

  const awayTeam = {
    name: data?.match_awayteam_name,
    logo: data?.team_away_badge,
    formation: data?.match_awayteam_system,
    players:
      data?.lineup?.away?.starting_lineups || [],
    coach: data?.lineup?.away?.coach || [],
  };

  const getPlayerRole = (position) => {
    const pos = Number(position);

    if (pos === 1) {
      return {
        role: "Goalkeeper",
        badge: "GK",
        color:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
      };
    }

    if (pos >= 2 && pos <= 5) {
      return {
        role: "Defender",
        badge: "DF",
        color:
          "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
      };
    }

    if (pos >= 6 && pos <= 8) {
      return {
        role: "Midfielder",
        badge: "MF",
        color:
          "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
      };
    }

    return {
      role: "Forward",
      badge: "FW",
      color:
        "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
    };
  };

  const TeamColumn = ({ team, side }) => (
    <div className="rounded-3xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900 overflow-hidden">

      {/* Team Header */}
      <div
        className={`relative overflow-hidden border-b border-zinc-200 p-5 dark:border-zinc-800 ${
          side === "home"
            ? "bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent"
            : "bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-transparent"
        }`}
      >

        <div className="flex items-center gap-4">

          {/* Logo */}
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-800">

            <img
              src={team?.logo}
              alt={team?.name}
              className="h-full w-full object-contain"
            />
          </div>

          {/* Team Info */}
          <div className="flex-1">

            <h2 className="text-xl font-bold text-zinc-900 dark:text-white">
              {team?.name}
            </h2>

            <div className="mt-2 flex items-center gap-2">

              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${
                  side === "home"
                    ? "bg-blue-500"
                    : "bg-purple-500"
                }`}
              >
                Formation {team?.formation}
              </span>

              <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                XI Players
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Players */}
      <div className="space-y-3 p-4">

        {team?.players?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">

            <Users className="mb-3 h-12 w-12 text-zinc-400" />

            <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-200">
              No Players Found
            </h3>

            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Lineup data unavailable
            </p>
          </div>
        ) : (
          team?.players?.map((player, index) => {
            const roleInfo = getPlayerRole(
              player?.lineup_position
            );

            return (
              <div
                key={index}
                className="group flex items-center justify-between rounded-2xl border border-zinc-200 bg-zinc-50 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-800/70"
              >

                {/* Left */}
                <div className="flex items-center gap-4">

                  {/* Jersey */}
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10">

                    <Shirt className="h-7 w-7 text-zinc-700 dark:text-zinc-300" />

                    <span className="absolute -bottom-1 rounded-full bg-zinc-900 px-2 py-0.5 text-[10px] font-bold text-white dark:bg-white dark:text-zinc-900">
                      #{player?.lineup_number || "0"}
                    </span>
                  </div>

                  {/* Player Info */}
                  <div>
                    <h3 className="text-sm font-bold text-zinc-900 dark:text-white md:text-base">
                      {player?.lineup_player}
                    </h3>

                    <div className="mt-1 flex items-center gap-2">

                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${roleInfo.color}`}
                      >
                        {roleInfo.badge}
                      </span>

                      <span className="text-xs text-zinc-500 dark:text-zinc-400">
                        {roleInfo.role}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Icon */}
                <div>
                  {Number(player?.lineup_position) === 1 ? (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-100 text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400">

                      <Shield className="h-5 w-5" />

                    </div>
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">

                      <UserCheck className="h-5 w-5" />

                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Coach */}
      {team?.coach?.length > 0 && (
        <div className="border-t border-zinc-200 p-4 dark:border-zinc-800">

          <div className="rounded-2xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-4">

            <div className="flex items-center justify-between gap-3">

              <div className="flex items-center gap-3">

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm dark:bg-zinc-800">

                  <Users className="h-5 w-5 text-blue-500" />

                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Head Coach
                  </p>

                  <h3 className="text-base font-bold text-zinc-900 dark:text-white">
                    {team?.coach?.[0]?.lineup_player}
                  </h3>
                </div>
              </div>

              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${
                  side === "home"
                    ? "bg-blue-500"
                    : "bg-purple-500"
                }`}
              >
                Coach
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Main Header */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">

        <div className="flex items-center gap-4">

          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/15 to-purple-500/15">

            <Users className="h-7 w-7 text-blue-500" />

          </div>

          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">
              Playing XI
            </h1>

            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Starting lineups for both teams
            </p>
          </div>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">

        {/* Home Team */}
        <TeamColumn
          team={homeTeam}
          side="home"
        />

        {/* Away Team */}
        <TeamColumn
          team={awayTeam}
          side="away"
        />

      </div>
    </div>
  );
}